import React, { useState, useContext } from 'react';
import { useTranslation } from "react-i18next";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Tooltip } from 'primereact/tooltip';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import CustomDropdown from '../../../../custom/customDropdown';
import { AuthContext } from '../../../../Header/auth - context';
import { useSelector } from 'react-redux';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const mesecOptions = [
    { value: 1, label: 'Januar' }, { value: 2, label: 'Februar' },
    { value: 3, label: 'Mart' }, { value: 4, label: 'April' },
    { value: 5, label: 'Maj' }, { value: 6, label: 'Jun' },
    { value: 7, label: 'Jul' }, { value: 8, label: 'Avgust' },
    { value: 9, label: 'September' }, { value: 10, label: 'Oktobar' },
    { value: 11, label: 'Novembar' }, { value: 12, label: 'Decembar' },
];

const emptyForm = {
    ID_zaposleni: null,
    Iznos: null,
    Valuta: null,
    Mesec: null,
    Godina: new Date().getFullYear(),
    Napomena: '',
};

const AdminZabraneTab = ({ adminZabraneDB, zaposleniDB, options, onRefresh, toast }) => {
  const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const company = useSelector(state => state.company);
    const [formData, setFormData] = useState(emptyForm);
    const [selectedId, setSelectedId] = useState(null);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);

    const zaposleniOptions = (zaposleniDB || []).map(z => ({ value: z.id, label: z.NazivZaposlenog }));

    const resetForm = () => { setFormData(emptyForm); setSelectedId(null); setFormSubmitted(false); };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleRowSelect = (row) => {
        const id = row?.id;
        setSelectedId(prev => {
            const next = String(prev) === String(id) ? null : id;
            if (next !== null) {
                setFormData({
                    ID_zaposleni: row.ID_zaposleni != null ? Number(row.ID_zaposleni) : null,
                    Iznos: row.Iznos != null ? Number(row.Iznos) : null,
                    Valuta: row.Valuta != null ? Number(row.Valuta) : null,
                    Mesec: row.Mesec != null ? Number(row.Mesec) : null,
                    Godina: row.Godina != null ? Number(row.Godina) : new Date().getFullYear(),
                    Napomena: row.Napomena || '',
                });
            } else {
                setFormData(emptyForm);
                setFormSubmitted(false);
            }
            return next;
        });
    };

    const handleSave = (e) => {
        e.preventDefault();
        setFormSubmitted(true);
        const { ID_zaposleni, Iznos, Valuta, Mesec, Godina } = formData;
        if (!ID_zaposleni || Iznos == null || !Valuta || Mesec == null || !Godina) {
            toast.current.show({ severity: 'warn', summary: t('admin.m_popunite_obavezna_polja'), life: 3000 });
            return;
        }
        confirmDialog({
            message: selectedId ? 'Sačuvati izmene?' : 'Dodati novu administrativnu zabranu?',
            acceptLabel: 'Da', rejectLabel: 'Ne',
            icon: 'pi pi-info-circle', acceptClassName: 'p-button-danger',
            accept: () => doSave(),
        });
    };

    const doSave = () => {
        fetch(process.env.REACT_APP_BACKEND_URL + '/trip/ManageObracunSifarnik', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body: JSON.stringify({ step: 'SAVE_ADMINZABRANE', id: selectedId || 0, formData }),
        })
            .then(async r => {
                const data = await r.json();
                if (!r.ok) throw new Error(data?.message || 'Greška!');
                return data;
            })
            .then(data => {
                const msg = data[0]?.message || '';
                if (msg.includes('Nema izmena')) {
                    toast.current.show({ severity: 'warn', summary: msg, life: 3000 });
                    return;
                }
                toast.current.show({ severity: 'success', summary: msg || 'Uspešno!', life: 3000 });
                resetForm();
                onRefresh();
            })
            .catch(err => toast.current.show({ severity: 'error', summary: err.message || 'Greška pri čuvanju!', life: 3000 }));
    };

    const handleDelete = () => {
        if (!selectedId) { toast.current.show({ severity: 'warn', summary: t('admin.m_izaberite_zapis_za_brisanje'), life: 3000 }); return; }
        confirmDialog({
            message: 'Obrisati izabranu administrativnu zabranu?',
            acceptLabel: 'Da', rejectLabel: 'Ne',
            icon: 'pi pi-info-circle', acceptClassName: 'p-button-danger',
            accept: () => {
                fetch(process.env.REACT_APP_BACKEND_URL + '/trip/ManageObracunSifarnik', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
                    body: JSON.stringify({ step: 'DELETE_ADMINZABRANE', id: selectedId }),
                })
                    .then(r => r.json())
                    .then(() => { toast.current.show({ severity: 'success', summary: t('admin.m_zabrana_obrisana'), life: 3000 }); resetForm(); onRefresh(); })
                    .catch(() => toast.current.show({ severity: 'error', summary: t('admin.m_greska_pri_brisanju'), life: 3000 }));
            },
        });
    };

    const exportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Admin zabrane');
        const headers = ['Zaposleni', 'Iznos', 'Valuta', 'Mesec', 'Godina', 'Napomena'];
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.eachCell(cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
            cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
        });
        (adminZabraneDB || []).forEach(r => {
            const mesecLabel = mesecOptions.find(m => m.value === r.Mesec)?.label || r.Mesec || '';
            const row = worksheet.addRow([
                r.ZaposleniNaziv || '',
                r.Iznos != null ? Number(r.Iznos) : '',
                r.ValutaNaziv || '',
                mesecLabel,
                r.Godina || '',
                r.Napomena || '',
            ]);
            row.eachCell(cell => {
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });
        worksheet.columns.forEach(col => {
            let max = 10;
            col.eachCell(cell => { if (cell.value) max = Math.max(max, String(cell.value).length); });
            col.width = max + 2;
        });
        const dt = new Date().toLocaleString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Belgrade' }).replace(/,/g, '');
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `Admin zabrane - ${company?.companyName || ''} - ${dt}.xlsx`);
    };

    const invalid = (val) => formSubmitted && (val === null || val === '' || val === undefined);

    const tableHeader = (
        <div className="export-buttons fakture-stavke-header">
            <Tooltip target=".export-buttons>button" position="bottom" />
            <div className="fakture-stavke-radnje">
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded onClick={exportExcel} data-pr-tooltip="XLS" />
                {selectedId && <Button type="button" icon="pi pi-trash" severity="danger" rounded onClick={handleDelete} data-pr-tooltip={t('common.delete')} />}
            </div>
            <input type="search" placeholder={t('common.search')} className="p-inputtext p-component"
                onInput={e => setGlobalFilter(e.target.value)} />
        </div>
    );

    return (
        <div style={{ marginTop: '1%' }}>
            <ConfirmDialog />
            <div className="blagajnaDashboard dnevniceDashboard">
                <Card>
                    <form autoComplete="off">
                        <div className="gridLicni">
                            <div className="col-12 md:col-6" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('finansije.zaposleni')}:</label>
                                <CustomDropdown name="ID_zaposleni" value={formData.ID_zaposleni}
                                    options={zaposleniOptions} optionLabel="label" placeholder={t('finansije.izaberiZaposlenog')}
                                    filter showClear
                                    required invalid={invalid(formData.ID_zaposleni)}
                                    onChange={handleChange} />
                            </div>
                            <div className="col-12 md:col-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('nova.iznos')}:</label>
                                <InputNumber name="Iznos" value={formData.Iznos}
                                    onValueChange={handleChange} minFractionDigits={2} maxFractionDigits={2}
                                    required className={invalid(formData.Iznos) ? 'p-invalid' : ''} />
                            </div>
                            <div className="col-12 md:col-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('nova.valuta')}:</label>
                                <CustomDropdown name="Valuta" value={formData.Valuta}
                                    options={options.ValuteDB} optionLabel="label" placeholder={t('nova.valuta')}
                                    required invalid={invalid(formData.Valuta)}
                                    onChange={handleChange} />
                            </div>
                            <div className="col-12 md:col-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('finansije.mesec')}:</label>
                                <CustomDropdown name="Mesec" value={formData.Mesec}
                                    options={mesecOptions} optionLabel="label" placeholder={t('nova.phMesec')}
                                    required invalid={invalid(formData.Mesec)} onChange={handleChange} />
                            </div>
                            <div className="col-12 md:col-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('finansije.godina')}:</label>
                                <InputNumber name="Godina" value={formData.Godina}
                                    onValueChange={handleChange} useGrouping={false}
                                    required className={invalid(formData.Godina) ? 'p-invalid' : ''} />
                            </div>
                            <div className="col-12 md:col-6" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('nova.napomena')}:</label>
                                <InputText name="Napomena" value={formData.Napomena} onChange={handleChange} />
                            </div>
                        </div>
                        <div className="formUnosZap buttonUnosZap">
                            <button type="submit" onClick={handleSave} style={{ minWidth: 'fit-content', marginTop: '2%' }} className="btn btn-success">
                                {selectedId ? 'SAČUVAJ' : 'UNOS'}
                            </button>
                        </div>
                    </form>
                </Card>
                <Card>
                    <DataTable className="stavke-tabela" value={adminZabraneDB || []} paginator rows={10} scrollable scrollHeight="45vh"
                        header={tableHeader} emptyMessage="Nema administrativnih zabrana" resizableColumns
                        globalFilter={globalFilter}
                        rowClassName={row => String(row.id) === String(selectedId) ? 'selected-row' : ''}>
                        <Column header={t('common.choose')} style={{ minWidth: '90px' }}
                            body={rowData => (
                                <Button label={t('common.choose')} icon="pi pi-eye" type="button"
                                    className="p-button-text p-button-sm" onClick={() => handleRowSelect(rowData)} />
                            )} />
                        <Column field="ZaposleniNaziv" header={t('finansije.zaposleni')} sortable style={{ minWidth: '180px' }} />
                        <Column field="Iznos" header={t('nova.iznos')} sortable style={{ minWidth: '120px' }}
                            body={r => r.Iznos != null ? `${Number(r.Iznos).toFixed(2)} ${r.ValutaNaziv || ''}` : '-'} />
                        <Column field="Mesec" header={t('finansije.mesec')} sortable style={{ minWidth: '100px' }}
                            body={r => mesecOptions.find(m => m.value === r.Mesec)?.label || r.Mesec || '-'} />
                        <Column field="Godina" header={t('finansije.godina')} sortable style={{ minWidth: '80px' }} />
                        <Column field="Napomena" header={t('nova.napomena')} style={{ minWidth: '200px' }} body={r => r.Napomena || '-'} />
                    </DataTable>
                </Card>
            </div>
        </div>
    );
};

export default AdminZabraneTab;
